import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import GoogleSignInButton, { isGoogleOAuthEnabled } from '../components/auth/GoogleSignInButton';
import '../styles/AuthPages.css';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
}

function RegisterPage() {
  const [formData, setFormData] = useState<RegisterFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { register, loginWithGoogle, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (isAuthenticated) {
    navigate('/');
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = (): boolean => {

    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }

    if (!formData.phoneNumber.trim()) {
      setError('Phone number is required');
      return false;
    }

    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!acceptTerms) {
      setError('You must accept the Terms and Conditions to continue');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLoading) return;

    if (!validateForm()) {
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      await register(
        formData.email,
        formData.phoneNumber,
        formData.password,
        formData.firstName,
        formData.lastName,
        true,
        new Date().toISOString()
      );
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = async (credential: string) => {
    if (isLoading) return;
    if (!acceptTerms) {
      setError('Please accept the Terms and Conditions to continue');
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      await loginWithGoogle(credential, true);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Google sign-up failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Create Your Account 🚀</h2>
          <p>Join Dandialift today</p>
        </div>

        {isGoogleOAuthEnabled && (
          <>
            <GoogleSignInButton
              text="signup_with"
              disabled={isLoading || !acceptTerms}
              onCredential={handleGoogleCredential}
              onError={setError}
            />
            {!acceptTerms && (
              <p className="oauth-hint">Accept the Terms and Conditions below to enable Google sign-up</p>
            )}
            <div className="oauth-divider"><span>or sign up with email</span></div>
          </>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Your first name"
                disabled={isLoading}
                autoComplete="given-name"
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Your last name"
                disabled={isLoading}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number *</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="Enter your phone number"
              required
              disabled={isLoading}
              autoComplete="tel"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a strong password"
                required
                disabled={isLoading}
                minLength={6}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? '👁️' : '🙈'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              required
              disabled={isLoading}
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => {
                  setAcceptTerms(e.target.checked);
                  if (error) setError('');
                }}
                disabled={isLoading}
                style={{ marginTop: '0.2rem' }}
              />
              <span>
                I accept the{' '}
                <button
                  type="button"
                  onClick={() => setShowTermsDialog(true)}
                  className="auth-link"
                  style={{ padding: 0, border: 'none', background: 'none', font: 'inherit', cursor: 'pointer' }}
                >
                  Terms and Conditions
                </button>
                {' '}and agree to the platform policies.
              </span>
            </label>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading || !formData.email || !formData.phoneNumber || !formData.password || !formData.confirmPassword || !acceptTerms}
          >
            {isLoading ? (
              <span className="loading-content">
                <span className="spinner"></span>
                Creating Account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div className="security-info">
          <h4>🔒 Account Security</h4>
          <ul>
            <li>Choose a strong password (minimum 6 characters)</li>
            <li>Use a strong password (minimum 6 characters)</li>
            <li>Provide a valid email for important notifications</li>
            <li>Your phone number helps with account recovery</li>
          </ul>
        </div>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="auth-link">
              Sign in here
            </Link>
          </p>
          <p style={{ marginTop: '0.5rem' }}>
            <Link to="/" className="auth-link">
              Back to Home
            </Link>
          </p>
        </div>
      </div>

      {showTermsDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="terms-dialog-title"
          onClick={() => setShowTermsDialog(false)}
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 id="terms-dialog-title" className="text-2xl font-semibold text-gray-900">
                  Terms and Conditions
                </h3>
                <p className="text-sm text-gray-500">Sample template for account registration</p>
              </div>
              <button
                type="button"
                onClick={() => setShowTermsDialog(false)}
                className="rounded-full px-3 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close terms dialog"
              >
                ✕
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-700">
              <p className="mb-4">
                Welcome to Dandialift. These Terms and Conditions explain the rules for using the platform.
                By creating an account, you agree to follow these terms.
              </p>

              <h4 className="mt-4 mb-2 text-base font-semibold text-gray-900">1. Account Responsibility</h4>
              <p className="mb-3">
                You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.
              </p>

              <h4 className="mt-4 mb-2 text-base font-semibold text-gray-900">2. Platform Use</h4>
              <p className="mb-3">
                You agree to use the service only for lawful ride-sharing and service-related activities, and to provide accurate information when creating trips or joining them.
              </p>

              <h4 className="mt-4 mb-2 text-base font-semibold text-gray-900">3. Safety and Conduct</h4>
              <p className="mb-3">
                Users must respect other members, follow applicable traffic laws, and avoid abusive, fraudulent, or harmful behavior while using the platform.
              </p>

              <h4 className="mt-4 mb-2 text-base font-semibold text-gray-900">4. Payments and Fees</h4>
              <p className="mb-3">
                Certain features may involve payments, service fees, or cancellations. You agree to any applicable charges shown before completing a transaction.
              </p>

              <h4 className="mt-4 mb-2 text-base font-semibold text-gray-900">5. Privacy</h4>
              <p className="mb-3">
                We may collect and process profile and trip data to operate the platform, improve services, and support user communication.
              </p>

              <h4 className="mt-4 mb-2 text-base font-semibold text-gray-900">6. Termination</h4>
              <p>
                We may suspend or terminate accounts that violate these terms, compromise security, or misuse the platform.
              </p>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setShowTermsDialog(false)}
                className="rounded-xl bg-gray-900 px-4 py-2 text-white transition-colors hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RegisterPage;

